// Task 7: Settings -> Scheduled Jobs. Sorted stale-first so a broken job is the first
// thing on screen. Inferred-fidelity rows show "last activity" and are visually
// marked: a run that found nothing leaves no trace, so their timestamps must not
// be read as run records. No job is inferred since breach sync and autopilot got
// run tables (Agent-Backend migration 070); the handling stays for any future one.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, Tag, Button, message } from "antd";
import styled from "styled-components";
import { jobsApi } from "./jobsApi";
import { SCALE } from "../../theme/claudeTokens";

// I3: "unknown" is a SIXTH value that exists only at the service/panel layer
// (src/jobs/service.py promotes it when the source query itself failed —
// staleness() in schedule.py keeps its own five-value contract untouched).
// Ordered near the top: an unreadable job needs attention as much as an
// overdue one, arguably more, since nobody can even see whether it ran.
const STALE_ORDER = {
  unknown: -1,
  overdue: 0,
  never_run: 1,
  running: 2,
  ok: 3,
  not_applicable: 4,
};
const STALE_LABEL = {
  unknown: "Unknown",
  overdue: "Overdue",
  never_run: "Never run",
  running: "Running",
  ok: "On schedule",
  // Rendered as muted text rather than a Tag — see the State column. A chip
  // containing only an em-dash reads as a broken control, and "—" told an
  // admin nothing about WHY the row has no state.
  not_applicable: "Not tracked",
};
// Status presets, not antd's stock hue names (red/green/orange/...): the root
// ConfigProvider only restyles colorSuccess/colorWarning/colorError/colorInfo,
// so a hue-named Tag would still paint antd's default palette (rule 12).
const STALE_COLOUR = {
  unknown: undefined,
  overdue: "error",
  never_run: "warning",
  running: "processing",
  ok: "success",
  not_applicable: "default",
};
// "unknown" needs a fourth hue antd has no status preset for — the palette's
// "active" triple, applied inline via the --odin-* variables.
const STALE_STYLE = {
  unknown: {
    background: "var(--odin-status-active-bg)",
    borderColor: "var(--odin-status-active-border)",
    color: "var(--odin-status-active-text)",
  },
};
const NOT_APPLICABLE_TITLE =
  "No run records for this job, so staleness cannot be judged — " +
  "absence of activity is not evidence of failure (src/jobs/schedule.py)";
const INFERRED_TITLE =
  "Inferred from work done — a run that found nothing leaves no trace, " +
  "so this is last activity, not a run record";

// I2: a job that failed can still write a recent completed_at/last_end
// (ce_scan/mfa_scan/directory_sync all write status='failed' TOGETHER WITH
// completed_at=NOW()), so staleness() — which only looks at timing — reads
// "ok". Anything other than a recognised success value is a failure for
// display purposes, regardless of what staleness() says. A routine skip is the
// one exception: it is a healthy no-op, see isRoutineSkip.
const SUCCESS_STATUSES = new Set(["completed", "ok", "success"]);
// A run that decided, correctly, to do nothing: a bank holiday, or the Management weekly digest
// on the weekdays after it has already gone out ("already sent this week"). Not a success and
// not a failure — it must never raise the attention count or turn the Outcome red. The backend
// also records a broken setup ("Teams not configured") as `skipped`, so the status alone
// cannot tell the two apart: only the API's `last_skip_routine === true` makes a skip neutral.
// A skip without that flag (false, or a backend that does not send it yet) stays a failure.
const isRoutineSkip = (r) =>
  r.last_status === "skipped" && r.last_skip_routine === true;
const isFailingRun = (r) =>
  !!r.last_status && !SUCCESS_STATUSES.has(r.last_status) && !isRoutineSkip(r);

// The row-level "this one is your problem" test. Deliberately NOT derived from
// rowWeight(): "running" sorts high because it is volatile, not because it is
// broken, so it must not inflate the attention count in the header.
const needsAttention = (r) =>
  r.staleness === "unknown" ||
  r.staleness === "overdue" ||
  r.staleness === "never_run" ||
  isFailingRun(r);

// Poll cadence while a triggered job is running — same idiom and interval as
// Fleet Radar's sweep polling (features/agents/components/scoping/FleetRadar.jsx).
const JOB_POLL_MS = 15000;

// Below this the grid scrolls inside its card instead of painting outside it.
// The original panel set no scroll.x at all, so a 972px table in a 762px card
// simply overflowed: the State tag and every Run-now button rendered OUTSIDE
// the card border and were unreachable at narrow widths.
const TABLE_SCROLL_X = 1120;

const relative = (iso) => {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(mins)) return "—";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
};

// C1: `next_fire` is always in the FUTURE, unlike `relative()`'s callers
// (last-run timestamps, always in the past). Reusing relative() on a future
// ISO string produced a negative minute count that was always < 60, so every
// row's Next column rendered as e.g. "-1200m ago". This is the signed
// counterpart, used ONLY for next_fire.
const until = (iso) => {
  if (!iso) return "—";
  const mins = Math.floor((new Date(iso).getTime() - Date.now()) / 60000);
  if (Number.isNaN(mins)) return "—";
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `in ${hours}h` : `in ${Math.floor(hours / 24)}d`;
};

// ── Presentation ────────────────────────────────────────────────────────────
// Table chrome comes from the root ConfigProvider (theme spec section 2.4); only
// layout rules and the attention rail live here.

const TableWrap = styled.div`
  .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  .ant-table-tbody > tr > td {
    vertical-align: middle;
  }

  /* Rows an admin has to act on carry a colour rail, so the eye lands on them
     before it reads a word. This is what the stale-first sort was for — it was
     previously defeated by the State tag rendering off-card entirely. */
  .jobs-row--attention > td:first-child {
    box-shadow: inset 3px 0 0 var(--odin-error);
  }
`;

const Toolbar = styled.div`
  align-items: baseline;
  border-bottom: 1px solid var(--odin-border);
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
`;

const Summary = styled.span`
  color: ${(p) => (p.$alert ? "var(--odin-error)" : "var(--odin-text-tertiary)")};
  font-size: 12px;
  font-weight: ${(p) => (p.$alert ? 600 : 400)};
`;

const Hint = styled.span`
  color: var(--odin-text-tertiary);
  font-size: 11px;
`;

const Footnote = styled.div`
  color: var(--odin-text-tertiary);
  font-size: 11px;
  line-height: 1.5;
  margin-top: 12px;
`;

const JobName = styled.div`
  color: var(--odin-text-primary);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
`;

const JobMeta = styled.div`
  color: var(--odin-text-tertiary);
  font-size: 11px;
  line-height: 1.4;
  margin-top: 2px;
`;

// Truncates rather than widening: KILL-switch env var names are long
// (SCOPING_FLEET_SWEEP_ENABLED) and used to blow the column out to 31% of the
// table for a value that is "—" in five rows out of eight.
const KillSwitch = styled.span`
  color: var(--odin-text-tertiary);
  display: block;
  font-family: ${SCALE.fontMono};
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Value = styled.span`
  color: var(--odin-text-primary);
  font-size: 13px;
  white-space: nowrap;
`;

const Muted = styled.span`
  color: var(--odin-text-tertiary);
  font-size: 13px;
`;

// The inferred-fidelity marker. A bare "*" next to a timestamp was invisible
// and its meaning lived only in a tooltip; this makes it a deliberate glyph
// and pairs it with the footnote legend below the table.
const InferredMark = styled.sup`
  color: var(--odin-warning);
  font-size: 11px;
  margin-left: 3px;
`;

export default function ScheduledJobsPanel() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-jobs"],
    queryFn: jobsApi.getJobs,
    // I7: poll while a triggered job is running, the way Fleet Radar polls
    // its sweep — gated off (false) whenever no row is running, so an idle
    // page does not poll forever.
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs || [];
      return jobs.some((r) => r.staleness === "running") ? JOB_POLL_MS : false;
    },
  });

  const run = useMutation({
    mutationFn: (key) => jobsApi.triggerJob(key),
    onSuccess: () => {
      message.success("Job started");
      qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    },
    // Task 6's dispatcher returns a plain 502 body (not a raised HTTPException) on a failed
    // MCP-backed trigger: {dispatched:"mcp", ok:false, error:"<real reason>"} — no `detail`
    // key. Read `error` first or that real reason is thrown away in favour of axios's generic
    // "Request failed with status code 502".
    onError: (e) =>
      message.error(
        e.response?.data?.error || e.response?.data?.detail || e.message,
      ),
    onSettled: () => setPending(null),
  });

  // I2: a failing last_status must surface even when staleness() itself
  // reads "ok" (a recent completed_at written alongside status='failed').
  // Only the "ok" bucket is affected — cap it just above "running" so a
  // failing-but-recently-completed row sorts above a genuinely healthy "ok"
  // row without disturbing overdue/never_run/running, which already sort
  // above "ok".
  const rowWeight = (r) => {
    const base = STALE_ORDER[r.staleness] ?? 9;
    return isFailingRun(r) ? Math.min(base, STALE_ORDER.ok - 0.5) : base;
  };

  const rows = useMemo(() => {
    const list = data?.jobs || [];
    return [...list].sort((a, b) => rowWeight(a) - rowWeight(b));
  }, [data]);

  const attention = rows.filter(needsAttention).length;
  const hasInferred = rows.some((r) => r.fidelity === "inferred");

  const columns = [
    {
      // Leftmost and loudest: this column is the reason the panel exists, and
      // it used to be the one clipped off the right-hand edge of the card.
      title: "State",
      key: "staleness",
      width: 120,
      render: (_v, r) =>
        r.staleness === "not_applicable" ? (
          <Muted title={NOT_APPLICABLE_TITLE}>{STALE_LABEL[r.staleness]}</Muted>
        ) : (
          <Tag
            color={STALE_COLOUR[r.staleness]}
            style={{ marginInlineEnd: 0, ...STALE_STYLE[r.staleness] }}
          >
            {STALE_LABEL[r.staleness]}
          </Tag>
        ),
    },
    {
      // Container folded in as subtext — it is orientation ("where do I read
      // the logs"), not a scanning key, and as its own column it cost 86px
      // while squeezing the job label into three wrapped lines.
      title: "Job",
      dataIndex: "label",
      key: "label",
      width: 280,
      render: (_v, r) => (
        <>
          <JobName>{r.label}</JobName>
          <JobMeta>{r.container}</JobMeta>
        </>
      ),
    },
    {
      // I6: spec-required kill-switch column — the env var name for the
      // three jobs that have one, "—" for the rest (itself useful info).
      title: "Kill switch",
      key: "kill_switch",
      width: 170,
      render: (_v, r) =>
        r.kill_switch ? (
          <KillSwitch title={r.kill_switch}>{r.kill_switch}</KillSwitch>
        ) : (
          <Muted>—</Muted>
        ),
    },
    {
      title: "Schedule",
      key: "schedule",
      width: 120,
      render: (_v, r) => <Value title={r.cron}>{r.schedule_human}</Value>,
    },
    {
      title: "Last run",
      key: "last",
      width: 120,
      render: (_v, r) => (
        <Value
          title={r.fidelity === "inferred" ? INFERRED_TITLE : r.last_end || ""}
        >
          {relative(r.last_end)}
          {r.fidelity === "inferred" ? <InferredMark>*</InferredMark> : ""}
        </Value>
      ),
    },
    {
      title: "Duration",
      key: "duration",
      width: 84,
      align: "right",
      render: (_v, r) =>
        r.duration_seconds == null ? (
          <Muted>—</Muted>
        ) : (
          <Value>{`${Math.round(r.duration_seconds)}s`}</Value>
        ),
    },
    {
      // Kept distinct from State on purpose: the two genuinely disagree, and
      // that disagreement is information. KQM reads last_status "ok" while
      // being 31 days overdue. State is the loud tag; Outcome is the quiet
      // one — except on failure, which is red (pinned by the I2 test).
      title: "Outcome",
      key: "outcome",
      width: 110,
      render: (_v, r) =>
        r.error ? (
          // I3: with the source query failed, the error text is the only
          // place the reason exists — surface it as the tag's tooltip.
          <Tag color="red" title={r.error} style={{ marginInlineEnd: 0 }}>
            unknown
          </Tag>
        ) : r.last_status ? (
          // The run's detail text ("bank holiday: …", "Graph mail not
          // configured") is the only place the reason for a skip lives.
          <Tag
            color={isFailingRun(r) ? "red" : undefined}
            title={r.last_detail || undefined}
            style={{ marginInlineEnd: 0 }}
          >
            {r.last_status}
          </Tag>
        ) : (
          <Muted>—</Muted>
        ),
    },
    {
      title: "Next",
      key: "next",
      width: 90,
      render: (_v, r) => <Value>{until(r.next_fire)}</Value>,
    },
    {
      title: "",
      key: "actions",
      width: 100,
      align: "right",
      render: (_v, r) => (
        <Button
          size="small"
          disabled={!r.can_trigger || pending === r.key}
          onClick={() => {
            setPending(r.key);
            run.mutate(r.key);
          }}
        >
          {pending === r.key ? "Starting…" : "Run now"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Toolbar>
        <Summary $alert={attention > 0}>
          {attention > 0
            ? `${attention} of ${rows.length} need${attention === 1 ? "s" : ""} attention`
            : rows.length > 0
              ? `All ${rows.length} jobs on schedule`
              : "No scheduled jobs"}
        </Summary>
        <Hint>All times Europe/London · hover a schedule for its cron</Hint>
      </Toolbar>

      <TableWrap>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="key"
          size="small"
          pagination={false}
          loading={isLoading}
          scroll={{ x: TABLE_SCROLL_X }}
          rowClassName={(r) => (needsAttention(r) ? "jobs-row--attention" : "")}
        />
      </TableWrap>

      {hasInferred && (
        <Footnote>
          <InferredMark>*</InferredMark> Last activity, inferred from work done
          — a run that found nothing leaves no trace, so this is not a run
          record.
        </Footnote>
      )}
    </>
  );
}
